import React, { useState } from "react";
import moment from "moment";
import {
  formatNumber,
  formatPercentage,
} from "@signolteam/shared/units-of-measure";
import { AppUtils } from "@src/utils";
import { useDCContext, useAuthContext } from "@src/context";
import { useManagerContext } from "@src/context/managerContext";
import ManagerHooks from "@src/context/hooks/managerHooks";
import { BackButton, Divider, Loader } from "@src/components/atoms";
import {
  ManagerDashboardInfo,
  FeedbackCard,
} from "@src/components/cards/manager";
import EmailsSection from "./EmailsSection";
import OnlineAccountSection from "./OnlineAccountSection";

export const ActivityPage = () => {
  // TODO - add tracking on feedback search
  const { profile } = useManagerContext();
  const { customer, behaviours } = profile;
  const auth = useAuthContext();
  const { user } = auth;
  const dcContext = useDCContext();
  const { getDynamicReplacement, existsDynamicReplacements } = dcContext;
  const isAviation = customer.industry === "aviation";

  const launchDate = customer.contractStartDate;
  //local states
  const [selectedExampleScreen, setSelectedExampleScreen] =
    useState("overview");
  const [selectedExampleTemplate, setSelectedExampleTemplate] =
    useState("REPORT_END_PERIOD");
  const [selectedNotificationPeriod, setSelectedNotificationPeriod] = useState(
    moment.utc().format("YYYY-MM"),
  );
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(0);

  const {
    data: { notificationTotals } = {},
    isPending: isPendingNotificationTotals,
    error: errorNotificationTotals,
  } = ManagerHooks.useGetNotificationTotals(auth.activeRole);
  const {
    data: { pageViews } = {},
    isPending: isPendingPageViews,
    error: errorPageViews,
  } = ManagerHooks.useGetPageViews(auth.activeRole);
  if (errorNotificationTotals || errorPageViews) {
    console.warn(
      "error encountered: ",
      errorNotificationTotals,
      errorPageViews,
    );
  }

  if (Object.keys(behaviours || {}).length === 0 || !user) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Loader size="2xl" />
      </div>
    );
  }

  const state = isPendingNotificationTotals
    ? "loading"
    : typeof notificationTotals?.rateSeen === "undefined"
      ? "invalid"
      : "valid";

  const emailNotificationOpenRate =
    state !== "valid"
      ? 0
      : formatPercentage(
          "0-1",
          notificationTotals.rateSeen > 0.99 ? 1 : notificationTotals.rateSeen,
        );
  const activityTooltipExists = existsDynamicReplacements(
    "fuel_manager__tooltip__overview_activity_tooltip",
  );

  //dynamic content
  const reportsDeliveredInfoCardText = getDynamicReplacement(
    "fuel_manager__overview__info_cards__reports_delivered_card",
  );
  const reportOpenRateInfoCardText = getDynamicReplacement(
    "fuel_manager__overview__info_cards__report_open_rate_card",
  );
  const emailTitle = getDynamicReplacement(
    "fuel_manager__activity__email_section__title",
  );
  const emailStatement = getDynamicReplacement(
    "fuel_manager__activity__email_section__statement",
  );
  const emailExamplesTitle = getDynamicReplacement(
    "fuel_manager__activity__email_section__examples_title",
  );
  const emailExamplesExpand = getDynamicReplacement(
    "fuel_manager__activity__email_section__examples_expand",
  );
  const emailScheduleTitle = getDynamicReplacement(
    "fuel_manager__activity__email_section__schedule_title",
  );
  const onlineAccountTitle = getDynamicReplacement(
    "fuel_manager__activity__online_account_section__title",
  );
  const onlineAccountStatement = getDynamicReplacement(
    "fuel_manager__activity__online_account_section__statement",
  );
  const onlineAccountExamplesTitle = getDynamicReplacement(
    "fuel_manager__activity__online_account_section__examples_title",
  );
  const onlineAccountExamplesExpand = getDynamicReplacement(
    "fuel_manager__activity__online_account_section__examples_expand",
  );
  const onlineAccountPageViewsTitle = getDynamicReplacement(
    "fuel_manager__activity__online_account_section__page_views_title",
  );
  const feedbackTitle = getDynamicReplacement(
    "fuel_manager__activity__feedback_section__title",
  );
  const feedbackStatement = getDynamicReplacement(
    "fuel_manager__activity__feedback_section__statement",
  );
  const activitySectionTooltipContent = activityTooltipExists
    ? [
        getDynamicReplacement(
          "fuel_manager__tooltip__overview_activity_tooltip",
        ),
      ]
    : [""];

  // Use eventPreferredName for aviation, but make it start with a capital letter. Use 'Report' for shipping customers
  const eventPreferredName = customer.preferredEventName;
  const eventPreferredNameCapitalized = !isAviation
    ? "Report"
    : eventPreferredName.charAt(0).toUpperCase() + eventPreferredName.slice(1);

  //Pie chart
  const pageViewsPieData = [];
  const pageViewsUrlMapping = {
    dashboard: "Overview",
    overview: "Overview",
    overview_operator: "Overview",
    operator: "Overview",
    details: `${eventPreferredNameCapitalized} Detail`,
    targets: "Goals",
    goals: "Goals",
    impact: "Impact",
  };
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const bvrColours = ["#B8CCFF", "#D9C4FD", "#BDF0E4", "#FFEEB9"];
  let colourIdx = 0;
  if (pageViews) {
    pageViews.forEach((datum) => {
      const segments =
        datum.url?.split("?")[0].split("/").filter(Boolean) || [];
      const lastSegment = segments[segments.length - 1];
      const secondToLastSegment = segments[segments.length - 2];
      const category =
        uuidRegex.test(lastSegment) && secondToLastSegment === "events"
          ? `${eventPreferredNameCapitalized} Detail`
          : (pageViewsUrlMapping[lastSegment] ?? null);
      if (category == null) {
        return;
      }
      const existingIdx = pageViewsPieData.findIndex(
        (p) => p.name === category,
      );
      if (existingIdx > -1) {
        //Merge with existing
        pageViewsPieData[existingIdx] = {
          ...pageViewsPieData[existingIdx],
          value: pageViewsPieData[existingIdx].value + datum.count,
        };
      } else {
        //Add new
        pageViewsPieData.push({
          name: category,
          value: datum.count,
          colour: bvrColours[colourIdx],
        });
        colourIdx++;
      }
    });
  }

  // example Dropdowns
  const exampleTemplateOptions = [
    {
      text: isAviation ? "Mid-Month Update" : "Mid-Period Update",
      value: "REPORT_MIDMONTHLY",
    },
    {
      text: isAviation ? "Monthly Recap" : "Period Recap",
      value: "REPORT_END_PERIOD",
    },
    {
      text: "Milestone",
      value: "MILESTONES",
    },
  ];
  const onChangeExampleTemplateImage = (e) => {
    setSelectedExampleTemplate(e);
  };
  const exampleScreenOptions = [
    {
      text: "Overview",
      value: "overview",
    },
    {
      text: "Goals",
      value: "goal",
    },
    {
      text: `${eventPreferredNameCapitalized} Detail`,
      value: "event_details",
    },
    {
      text: "Impact",
      value: "impact",
    },
  ];
  const onChangeExampleScreenImage = (e) => {
    setSelectedExampleScreen(e);
  };

  const monthsList = AppUtils.getMonthsSinceLaunchDate(launchDate).map((m) =>
    moment.utc(m).valueOf(),
  );

  const notificationPeriodOptions = monthsList.map((date) => {
    return {
      text: moment.utc(date).format("MMM YYYY"),
      value: moment.utc(date).format("YYYY-MM"),
    };
  });
  notificationPeriodOptions.reverse();

  const onChangeSelectedNotificationPeriod = (e) => {
    setSelectedCalendarDay(0); //reset selected calendar day
    setSelectedNotificationPeriod(e);
  };

  const onChangeSelectedCalendarDay = (e) => {
    setSelectedCalendarDay(e);
  };

  return (
    <div>
      <section className="px-3 md:px-12 pt-6 flex flex-col items-center">
        <div className="self-start">
          <BackButton />
        </div>
        <div className="w-full mb-6">
          <div className="w-full flex justify-center">
            <div className="font-extrabold text-2xl md:text-4xl m-0 mb-6">
              {emailTitle}
            </div>
          </div>
          <div className="w-full text-center mb-6">{emailStatement}</div>
          <div className="flex gap-4 justify-center">
            <ManagerDashboardInfo
              highlight={true}
              text={reportsDeliveredInfoCardText}
              value={formatNumber(notificationTotals?.countDelivered ?? 0)}
              isLoading={isPendingNotificationTotals}
            />
          </div>
        </div>
        <div className="w-full mb-12 flex justify-center align-center">
          <div className="w-full h-full">
            <div className="flex w-full justify-center">
              <EmailsSection
                isLoading={false}
                industry={customer.industry}
                exampleTitle={emailExamplesTitle}
                exampleExpand={emailExamplesExpand}
                exampleDropdownOnChange={onChangeExampleTemplateImage}
                exampleTemplateOptions={exampleTemplateOptions}
                exampleTemplateImage={selectedExampleTemplate}
                scheduleTitle={emailScheduleTitle}
                notificationPeriodOptions={notificationPeriodOptions}
                selectedNotificationPeriod={selectedNotificationPeriod}
                selectedNotificationPeriodOnChange={
                  onChangeSelectedNotificationPeriod
                }
                selectedDay={selectedCalendarDay}
                selectedDayOnChange={onChangeSelectedCalendarDay}
                teamId={undefined}
              />
            </div>
          </div>
        </div>
      </section>
      <Divider />
      <section className="px-3 md:px-12 pt-6 flex flex-col items-center">
        <div className="w-full mb-6">
          <div className="w-full flex justify-center">
            <div className="font-extrabold text-2xl md:text-4xl m-0 mb-6">
              {onlineAccountTitle}
            </div>
          </div>
          <div className="w-full text-center">{onlineAccountStatement}</div>
        </div>
        <div className="w-full mb-12 flex justify-center align-center">
          {isPendingPageViews ? (
            <Loader size="2xl" />
          ) : (
            <div className="w-full h-full">
              <div className="flex w-full justify-center">
                <OnlineAccountSection
                  industry={customer.industry}
                  exampleTitle={onlineAccountExamplesTitle}
                  exampleExpand={onlineAccountExamplesExpand}
                  exampleDropdownOnChange={onChangeExampleScreenImage}
                  exampleScreenOptions={exampleScreenOptions}
                  exampleScreenImage={selectedExampleScreen}
                  pageViewsTitle={onlineAccountPageViewsTitle}
                  pageViews={pageViewsPieData}
                  isLoading={isPendingPageViews}
                />
              </div>
            </div>
          )}
        </div>
      </section>
      <Divider />
      <section className="px-3 md:px-12 pt-6 flex flex-col items-center">
        <div className="w-full mb-6">
          <div className="w-full flex flex-col items-center">
            <div className="font-extrabold text-2xl md:text-4xl m-0 mb-6">
              {feedbackTitle}
            </div>
            <div className="md:w-2/3 w-full text-center">
              {feedbackStatement}
            </div>
          </div>
        </div>
        <div
          className="w-full mb-12 justify-center align-center block"
          style={{ minHeight: "560px" }}
        >
          <FeedbackCard />
        </div>
      </section>
    </div>
  );
};
